import React from 'react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/common/Button';
import { Icon } from '@/components/common/Icon';
import { formatDistanceToNow } from 'date-fns';
import type { AppNotification } from '../types';
import { markAsRead } from '../utils/notification-actions';
import { useAppStore } from '@/store/useAppStore';

// Type-specific card imports
import { EventCreatedCard } from './cards/EventCreatedCard';
import { MentionCard } from './cards/MentionCard';
import { ImportCompleteCard } from './cards/ImportCompleteCard';
import { RosterUpdateCard } from './cards/RosterUpdateCard';

const ICON_MAP: Record<string, string> = {
  'event-created': 'event',
  'mention': 'alternate_email',
  'import-complete': 'task_alt',
  'roster-update': 'swap_horiz',
  'invite-update-request': 'mark_email_read',
  'roster-resolution-request': 'manage_search',
};

interface Props {
  notification: AppNotification;
  onNavigate?: (path: string) => void;
}

export const NotificationCard: React.FC<Props> = ({ notification, onNavigate }) => {
  const user = useAppStore(state => state.auth.user);
  const icon = notification.icon || ICON_MAP[notification.type] || 'notifications';
  const timeAgo = notification.createdAt?.toDate
    ? formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true })
    : '';

  const handleClick = async () => {
    if (!notification.read && user?.uid) {
      await markAsRead(user.uid, notification.id);
    }
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Notification cards may contain nested action buttons; card-level keyboard semantics need a separate notification action model.
    <div
      onClick={() => { void handleClick(); }}
      className={cn(
        'flex w-full gap-3 rounded-none border-b border-gray-200 px-4 py-3 text-left dark:border-border-dark',
        'hover:bg-gray-50 dark:hover:bg-white/5 transition-colors',
        !notification.read && 'bg-primary/5 dark:bg-primary/10',
      )}
    >
      {/* Icon */}
      <div className={cn(
        'shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
        !notification.read
          ? 'bg-primary/10 text-primary'
          : 'bg-gray-100 dark:bg-white/10 text-gray-400',
      )}>
        <Icon name={icon} size="sm" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={cn(
            'text-sm leading-snug',
            !notification.read
              ? 'font-semibold text-gray-900 dark:text-white'
              : 'font-medium text-gray-600 dark:text-gray-400',
          )}>
            {notification.title}
          </p>
          {!notification.read && (
            <span className="shrink-0 w-2 h-2 mt-1.5 rounded-full bg-primary" />
          )}
        </div>

        {/* Type-specific content */}
        <TypeSpecificContent notification={notification} onNavigate={onNavigate} />

        {/* Timestamp + project */}
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          {notification.projectTitle} · {timeAgo}
        </p>
      </div>
    </div>
  );
};

/** Route to the correct type-specific card */
const TypeSpecificContent: React.FC<Props> = ({ notification, onNavigate }) => {
  const requestId = typeof notification.payload?.requestId === 'string' ? notification.payload.requestId : '';
  const reviewId = typeof notification.payload?.reviewId === 'string' ? notification.payload.reviewId : '';

  switch (notification.type) {
    case 'event-created':
      return <EventCreatedCard notification={notification} onNavigate={onNavigate} />;
    case 'mention':
      return <MentionCard notification={notification} onNavigate={onNavigate} />;
    case 'import-complete':
      return <ImportCompleteCard notification={notification} onNavigate={onNavigate} />;
    case 'roster-update':
      return <RosterUpdateCard notification={notification} />;
    case 'invite-update-request':
      return <ReviewRequestCard
        notification={notification}
        onNavigate={onNavigate}
        path={`/settings?panel=project-access&requestId=${requestId}`}
        actionLabel="Review request"
      />;
    case 'roster-resolution-request':
      return <ReviewRequestCard
        notification={notification}
        onNavigate={onNavigate}
        path={`/admin/roster?reviewId=${reviewId}`}
        actionLabel="Open roster"
      />;
    default:
      return notification.body
        ? <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{notification.body}</p>
        : null;
  }
};

interface ReviewRequestCardProps extends Props {
  path: string;
  actionLabel: string;
}

const ReviewRequestCard: React.FC<ReviewRequestCardProps> = ({
  notification,
  onNavigate,
  path,
  actionLabel,
}) => {
  const handleNavigate = () => {
    onNavigate?.(path);
  };

  return (
    <div className="mt-1">
      {notification.body ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{notification.body}</p>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleNavigate}
        className={cn(
          'mt-1.5 min-h-0 rounded px-2 py-0.5 text-xs font-medium',
          'bg-primary/10 text-primary hover:bg-primary/20 transition-colors',
        )}
      >
        {actionLabel}
      </Button>
    </div>
  );
};
